import {useCallback, useEffect, useRef} from "react";

/**
 * История изменений для текстового поля (Ctrl+Z / Ctrl+Y).
 * Синхронизация с внешним value только при загрузке с сервера, без сброса при undo.
 */
export function useTextHistory(externalValue: string, onExternalChange: (value: string) => void) {
    const historyRef = useRef<string[]>([]);
    const indexRef = useRef(0);
    const fromHistoryActionRef = useRef(false);
    const lastEmittedRef = useRef(externalValue);
    const initializedRef = useRef(false);

    if (!initializedRef.current) {
        initializedRef.current = true;
        historyRef.current = [externalValue];
        indexRef.current = 0;
        lastEmittedRef.current = externalValue;
    }

    const initHistory = useCallback((value: string) => {
        historyRef.current = [value];
        indexRef.current = 0;
        lastEmittedRef.current = value;
    }, []);

    useEffect(() => {
        if (fromHistoryActionRef.current) {
            fromHistoryActionRef.current = false;
            lastEmittedRef.current = externalValue;
            return;
        }
        if (externalValue === lastEmittedRef.current) {
            return;
        }
        initHistory(externalValue);
    }, [externalValue, initHistory]);

    const pushHistory = useCallback((value: string) => {
        const trimmedStack = historyRef.current.slice(0, indexRef.current + 1);
        if (trimmedStack[trimmedStack.length - 1] === value) {
            return;
        }
        trimmedStack.push(value);
        historyRef.current = trimmedStack;
        indexRef.current = trimmedStack.length - 1;
    }, []);

    const setValue = useCallback(
        (value: string) => {
            pushHistory(value);
            lastEmittedRef.current = value;
            onExternalChange(value);
        },
        [onExternalChange, pushHistory],
    );

    const undo = useCallback(() => {
        if (indexRef.current <= 0) {
            return false;
        }
        indexRef.current -= 1;
        const value = historyRef.current[indexRef.current];
        fromHistoryActionRef.current = true;
        lastEmittedRef.current = value;
        onExternalChange(value);
        return true;
    }, [onExternalChange]);

    const redo = useCallback(() => {
        if (indexRef.current >= historyRef.current.length - 1) {
            return false;
        }
        indexRef.current += 1;
        const value = historyRef.current[indexRef.current];
        fromHistoryActionRef.current = true;
        lastEmittedRef.current = value;
        onExternalChange(value);
        return true;
    }, [onExternalChange]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const mod = e.metaKey || e.ctrlKey;
            if (!mod) {
                return;
            }
            const key = e.key.toLowerCase();
            if (key === "z" && !e.shiftKey) {
                if (undo()) {
                    e.preventDefault();
                }
                return;
            }
            if (key === "y" || (key === "z" && e.shiftKey)) {
                if (redo()) {
                    e.preventDefault();
                }
            }
        },
        [undo, redo],
    );

    return {setValue, undo, redo, handleKeyDown, initHistory};
}
